"use client";

import React from "react";
import {
  Button,
  EmptyState,
  Badge,
  Checkbox,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  LoadingSpinner,
} from "@west-tennessee-consulting/ui";
import { Folder, Eye, Download, ChevronDown } from "lucide-react";
import { getFileIcon } from "../utils/file-icons";
import { getExportFormats } from "../utils/export-formats";

interface FileListViewProps {
  files: any[];
  accumulatedFiles: any[] | null;
  isDownloading: string | null;
  allowDownload: boolean;
  allowView: boolean;
  onFolderSelect: (folder: any) => void;
  onFileAction: (
    file: any,
    action: "view" | "download" | "select" | "preview",
    exportFormat?: string
  ) => void;
  nextPageToken?: string;
  onLoadMore: () => void;
  selectable?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
}

export function FileListView({
  files,
  accumulatedFiles,
  isDownloading,
  allowDownload,
  allowView,
  onFolderSelect,
  onFileAction,
  nextPageToken,
  onLoadMore,
  selectable = false,
  selectedIds,
  onToggleSelect,
}: FileListViewProps) {
  const displayFiles = accumulatedFiles || files;

  if (displayFiles.length === 0) {
    return (
      <EmptyState
        icon={Folder}
        title="Empty Folder"
        description="This folder contains no files or subfolders"
      />
    );
  }

  return (
    <>
      <div className="space-y-2">
        {displayFiles.map((file) => (
          <div
            key={file.id}
            data-selected={selectable && selectedIds?.has(file.id) ? "true" : undefined}
            className="flex items-center justify-between p-2 sm:p-3 border rounded-lg hover:bg-muted/50 transition-colors data-[selected=true]:border-primary data-[selected=true]:bg-primary/5"
          >
            {selectable && (
              <div className="flex-shrink-0 pr-2" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={selectedIds?.has(file.id) ?? false}
                  onCheckedChange={() => onToggleSelect?.(file.id)}
                  aria-label={`Select ${file.name}`}
                />
              </div>
            )}
            <div
              className="flex items-center gap-2 sm:gap-3 flex-1 cursor-pointer min-w-0"
              onClick={() => (file.isFolder ? onFolderSelect(file) : onFileAction(file, "preview"))}
            >
              <div className="flex-shrink-0">{getFileIcon(file.mimeType, file.isFolder)}</div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm sm:text-base truncate">{file.name}</div>
                <div className="text-xs text-muted-foreground flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <div className="flex items-center gap-1 sm:gap-2">
                    {file.formattedSize && <span>{file.formattedSize}</span>}
                    {file.formattedModified && (
                      <span className="hidden sm:inline">Modified {file.formattedModified}</span>
                    )}
                  </div>
                  {file.shared && (
                    <Badge variant="secondary" className="text-xs w-fit">
                      Shared
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {!file.isFolder && (
              <div className="flex items-center gap-1 flex-shrink-0">
                {allowView && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-1 sm:p-2"
                          onClick={() => onFileAction(file, "preview")}
                        >
                          <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Preview file</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                {allowDownload && (file.canDownload || file.isGoogleDoc) && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        {file.isGoogleDoc ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="p-1 sm:p-2"
                                disabled={isDownloading === file.id}
                              >
                                {isDownloading === file.id ? (
                                  <LoadingSpinner className="h-3 w-3 sm:h-4 sm:w-4" />
                                ) : (
                                  <>
                                    <Download className="h-3 w-3 sm:h-4 sm:w-4" />
                                    <ChevronDown className="h-2 w-2 sm:h-3 sm:w-3 ml-0.5" />
                                  </>
                                )}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              {getExportFormats(file.mimeType).map((format) => (
                                <DropdownMenuItem
                                  key={format.format}
                                  onClick={() => onFileAction(file, "download", format.format)}
                                  className="flex flex-col items-start p-2"
                                >
                                  <div className="font-medium text-sm">{format.label}</div>
                                  <div className="text-xs text-muted-foreground">
                                    {format.description}
                                  </div>
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1 sm:p-2"
                            onClick={() => onFileAction(file, "download")}
                            disabled={isDownloading === file.id}
                          >
                            {isDownloading === file.id ? (
                              <LoadingSpinner className="h-3 w-3 sm:h-4 sm:w-4" />
                            ) : (
                              <Download className="h-3 w-3 sm:h-4 sm:w-4" />
                            )}
                          </Button>
                        )}
                      </TooltipTrigger>
                      <TooltipContent>
                        {file.isGoogleDoc ? "Export file" : "Download file"}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {nextPageToken && (
        <div className="flex justify-center py-2 sm:py-3">
          <Button variant="outline" size="sm" onClick={onLoadMore} className="w-full sm:w-auto">
            Load more
          </Button>
        </div>
      )}
    </>
  );
}
